import { useEffect, useId, useMemo, useState } from "react";
import axios from "axios";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ReferenceArea,
    ReferenceLine,
} from "recharts";
import PercentileScale from "@/components/PercentileScale";
import { METRIC } from "@/constants/testIds";

const TEAL = "#B8D4D0";
const SAGE = "#9AADA8";
const PINK = "#d99696";

// Make the shaded periods clearly visible against the dark olive background
const SHADE_FILL = "rgba(184, 212, 208, 0.13)";
const SHADE_STROKE = "rgba(184, 212, 208, 0.38)";

export default function MetricCard({ metricId, summary, api }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const gradId = useId().replace(/:/g, "_");

    useEffect(() => {
        let alive = true;
        setData(null);
        setError(null);
        const fetchData = async () => {
            try {
                const r = await axios.get(`${api}/metrics/${metricId}`);
                if (alive) setData(r.data);
            } catch (e) {
                if (alive) setError(e.message);
            }
        };
        fetchData();
        return () => {
            alive = false;
        };
    }, [api, metricId]);

    const chartData = useMemo(() => {
        if (!data) return [];
        return data.series
            .filter((d) => d.net_pct_oi != null)
            .map((d) => ({
                date: d.date,
                t: new Date(d.date).getTime(),
                value: d.net_pct_oi,
            }));
    }, [data]);

    // y-domain & 0-crossing offset for the split-color gradient
    const { yMin, yMax, zeroOffset } = useMemo(() => {
        if (!chartData.length)
            return { yMin: -1, yMax: 1, zeroOffset: 0.5 };
        const vs = chartData.map((d) => d.value);
        const lo = Math.min(...vs);
        const hi = Math.max(...vs);
        if (hi <= 0) return { yMin: lo, yMax: hi, zeroOffset: 0 };
        if (lo >= 0) return { yMin: lo, yMax: hi, zeroOffset: 1 };
        const off = hi / (hi - lo); // y=0 falls at this fraction from top
        return { yMin: lo, yMax: hi, zeroOffset: off };
    }, [chartData]);

    const isRussell = summary.market_key.includes("Russell");
    const cohorts = data?.metric?.wow_cohorts || ["asset_mgrs", "leveraged_funds"];

    const latest = data?.current;
    const latestSide = latest?.net_pct_oi != null
        ? latest.net_pct_oi > 0 ? "Net Long" : "Net Short"
        : null;

    return (
        <article
            data-testid={METRIC.card(metricId)}
            className="metric-card-frame"
        >
            {/* ---------- Card head ---------- */}
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="pord-eyebrow">
                        card {metricId} · {summary.market_key.toLowerCase()}
                    </div>
                    <h2 className="pord-heading mt-3 text-2xl sm:text-3xl text-white">
                        {summary.display_name}
                    </h2>
                    <p className="pord-body mt-2 text-sm">{summary.notes}</p>
                    {data?.metric?.description && (
                        <p className="pord-body mt-3 max-w-2xl" style={{ fontSize: 14, lineHeight: 1.75, color: "rgba(200,205,195,0.82)" }}>
                            {data.metric.description}
                        </p>
                    )}
                </div>
                {latest && (
                    <div className="text-right">
                        <div className="pord-label">latest · {latest.date}</div>
                        <div
                            className="text-3xl font-light tabular-nums mt-1 uppercase"
                            style={{ color: TEAL, letterSpacing: "0.06em" }}
                        >
                            {fmtRank(latest.pct_rank_52w)} Pctile
                        </div>
                        <div className="pord-label mt-0.5">52-week Lookback</div>
                    </div>
                )}
            </div>

            {/* ---------- Percentile scale card (above chart) ---------- */}
            {data?.percentile_scale && latest && (
                <div className="mt-8">
                    <div
                        className="rounded-2xl px-6 sm:px-8 py-7 border"
                        style={{
                            backgroundColor: "rgba(255, 255, 255, 0.035)",
                            borderColor: "rgba(255,255,255,0.07)",
                        }}
                    >
                        <PercentileScale
                            metricId={metricId}
                            scale={data.percentile_scale}
                            current={latest}
                        />
                    </div>
                </div>
            )}

            {/* ---------- Narrative (between scale and chart) ---------- */}
            {data?.narrative && (
                <div className="mt-8" data-testid={METRIC.interpretation(metricId)}>
                    <div className="pord-label mb-3">read</div>
                    <p className="pord-body text-[15px]" style={{ color: "#ffffff" }}>
                        {data.narrative.dynamic}
                    </p>
                    {data?.percentile_scale && (
                        <p
                            className="pord-body text-[14px] mt-3"
                            style={{ color: "#ffffff" }}
                        >
                            1SD at 16th percentile : {fmtPct(data.percentile_scale.p16)} OI, and at 84th Percentile : {fmtPct(data.percentile_scale.p84)} OI. Over 2SD to the right considered extreme long, to the left considered extreme short (more potential convergence in the underlying assets).
                        </p>
                    )}
                    {data.narrative.static && (
                        <p
                            className="pord-body text-[14px] mt-3"
                            style={{ color: "#ffffff" }}
                        >
                            {data.narrative.static} As seen in charting below.
                        </p>
                    )}
                    <p
                        className="pord-body text-[14px] mt-3"
                        style={{ color: "var(--pord-text-muted)" }}
                    >
                        OI = total open interest = total open futures contracts of a given category.
                    </p>
                </div>
            )}

            {/* ---------- Chart ---------- */}
            <div className="mt-10">
                {/* Latest tag — top-right of chart */}
                {latest && latestSide && (
                    <div className="flex justify-end mb-2 pr-2">
                        <span
                            className="text-sm font-light tabular-nums"
                            style={{ color: "rgba(255,255,255,0.85)" }}
                        >
                            <span className="pord-label mr-2">latest</span>
                            <span
                                style={{
                                    color:
                                        latest.net_pct_oi > 0 ? TEAL : PINK,
                                    fontWeight: 400,
                                }}
                            >
                                {latestSide}
                            </span>
                            <span className="text-white/40 mx-2">&middot;</span>
                            <span>{fmtPct(latest.net_pct_oi)} net %OI</span>
                        </span>
                    </div>
                )}

                <div
                    data-testid={METRIC.chart(metricId)}
                    className="h-[340px] w-full"
                >
                    {chartData.length === 0 ? (
                        <div className="pord-body h-full flex items-center justify-center">
                            {error ? `error: ${error}` : "loading\u2026"}
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                                data={chartData}
                                margin={{ top: 18, right: 20, bottom: 0, left: -6 }}
                            >
                                <defs>
                                    {/* Split gradient: TEAL above 0, PINK below */}
                                    <linearGradient
                                        id={`fill_${gradId}`}
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="0"
                                            stopColor={TEAL}
                                            stopOpacity={0.45}
                                        />
                                        <stop
                                            offset={zeroOffset}
                                            stopColor={TEAL}
                                            stopOpacity={0.04}
                                        />
                                        <stop
                                            offset={zeroOffset}
                                            stopColor={PINK}
                                            stopOpacity={0.04}
                                        />
                                        <stop
                                            offset="1"
                                            stopColor={PINK}
                                            stopOpacity={0.45}
                                        />
                                    </linearGradient>
                                    <linearGradient
                                        id={`stroke_${gradId}`}
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop offset="0" stopColor={TEAL} />
                                        <stop
                                            offset={zeroOffset}
                                            stopColor={TEAL}
                                        />
                                        <stop
                                            offset={zeroOffset}
                                            stopColor={PINK}
                                        />
                                        <stop offset="1" stopColor={PINK} />
                                    </linearGradient>
                                </defs>

                                <CartesianGrid
                                    stroke="rgba(255,255,255,0.06)"
                                    vertical={false}
                                />
                                <XAxis
                                    dataKey="t"
                                    type="number"
                                    scale="time"
                                    domain={["dataMin", "dataMax"]}
                                    tickFormatter={(t) =>
                                        new Date(t).toLocaleDateString("en-US", {
                                            year: "numeric",
                                            month: "short",
                                        })
                                    }
                                    tickLine={false}
                                    axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                                    minTickGap={60}
                                />
                                <YAxis
                                    tickFormatter={(v) => `${v.toFixed(0)}%`}
                                    tickLine={false}
                                    axisLine={false}
                                    width={60}
                                />
                                <Tooltip
                                    content={<ChartTooltip />}
                                    cursor={{
                                        stroke: TEAL,
                                        strokeOpacity: 0.3,
                                    }}
                                />

                                {/* Shaded periods */}
                                {data?.shaded_periods?.map((p, i) => (
                                    <ReferenceArea
                                        key={i}
                                        x1={new Date(p.start).getTime()}
                                        x2={new Date(p.end).getTime()}
                                        fill={SHADE_FILL}
                                        stroke={SHADE_STROKE}
                                        strokeDasharray="2 3"
                                        label={(props) =>
                                            renderShadeLabel(props, p.label, p.sub)
                                        }
                                        ifOverflow="extendDomain"
                                    />
                                ))}

                                {/* RTY reconstitution lines */}
                                {isRussell &&
                                    data?.reconstitution_lines?.map((r, i) => (
                                        <ReferenceLine
                                            key={i}
                                            x={new Date(r.date).getTime()}
                                            stroke={SAGE}
                                            strokeDasharray="3 3"
                                            strokeOpacity={0.85}
                                            label={{
                                                value: "RTY rebal",
                                                position: "insideBottomRight",
                                                fill: SAGE,
                                                fontSize: 10,
                                                offset: 8,
                                            }}
                                        />
                                    ))}

                                <ReferenceLine
                                    y={0}
                                    stroke="rgba(255,255,255,0.22)"
                                    strokeDasharray="1 3"
                                />

                                <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke={`url(#stroke_${gradId})`}
                                    strokeWidth={1.6}
                                    fill={`url(#fill_${gradId})`}
                                    dot={false}
                                    activeDot={{
                                        r: 4,
                                        fill: "#fff",
                                        stroke: "#222518",
                                        strokeWidth: 2,
                                    }}
                                    isAnimationActive={false}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            {/* ---------- WoW table ---------- */}
            <div className="mt-10">
                <WowTable metricId={metricId} current={latest} cohorts={cohorts} />
            </div>
        </article>
    );
}

function renderShadeLabel(props, label, sub) {
    const { viewBox } = props;
    if (!viewBox) return null;
    const { x, y, width } = viewBox;
    const cx = x + width / 2;
    return (
        <g>
            <text
                x={cx}
                y={y + 12}
                fill="rgba(184, 212, 208, 0.85)"
                fontSize={10.5}
                fontWeight={400}
                textAnchor="middle"
                style={{ letterSpacing: "0.04em" }}
            >
                {label}
            </text>
            <text
                x={cx}
                y={y + 26}
                fill="rgba(184, 212, 208, 0.55)"
                fontSize={9.5}
                textAnchor="middle"
                style={{ letterSpacing: "0.12em", textTransform: "uppercase" }}
            >
                {sub}
            </text>
        </g>
    );
}

function fmtPct(v) {
    if (v == null) return "\u2014";
    const sign = v > 0 ? "+" : "";
    return `${sign}${Number(v).toFixed(2)}%`;
}

function fmtRank(v) {
    if (v == null) return "\u2014";
    const r = Math.round(v);
    const m100 = r % 100;
    const m10 = r % 10;
    const suf =
        m100 >= 11 && m100 <= 13
            ? "th"
            : m10 === 1
              ? "st"
              : m10 === 2
                ? "nd"
                : m10 === 3
                  ? "rd"
                  : "th";
    return `${r}${suf}`;
}

function ChartTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    const c = p.value >= 0 ? TEAL : PINK;
    return (
        <div
            className="pord-surface px-4 py-3 text-xs"
            style={{ background: "rgba(26,29,18,0.92)" }}
        >
            <div className="pord-label mb-1">{p.date}</div>
            <div className="tabular-nums" style={{ color: c }}>
                net %OI: {fmtPct(p.value)}
            </div>
        </div>
    );
}

function WowTable({ metricId, current, cohorts }) {
    return (
        <div data-testid={METRIC.oiTable(metricId)} className="pord-surface p-5 max-w-[50%]">
            <div className="pord-label mb-4">
                current week &middot; % position change week-o-week
            </div>
            <table className="w-full text-sm tabular-nums">
                <thead>
                    <tr className="text-left text-white/40">
                        <th className="font-light pb-3 pord-label">cohort</th>
                        <th className="font-light pb-3 text-right pord-label">long</th>
                        <th className="font-light pb-3 text-right pord-label">short</th>
                    </tr>
                </thead>
                <tbody className="text-white/90 font-light">
                    {cohorts.includes("asset_mgrs") && (
                        <tr className="border-t border-white/[0.06]">
                            <td className="py-2.5">asset mgrs</td>
                            <td className="py-2.5 text-right">
                                <Delta v={current?.am_long_wow} />
                            </td>
                            <td className="py-2.5 text-right">
                                <Delta v={current?.am_short_wow} />
                            </td>
                        </tr>
                    )}
                    {cohorts.includes("leveraged_funds") && (
                        <tr className="border-t border-white/[0.06]">
                            <td className="py-2.5">leveraged funds</td>
                            <td className="py-2.5 text-right">
                                <Delta v={current?.lm_long_wow} />
                            </td>
                            <td className="py-2.5 text-right">
                                <Delta v={current?.lm_short_wow} />
                            </td>
                        </tr>
                    )}
                    {cohorts.includes("individual_investors") && (
                        <tr className="border-t border-white/[0.06]">
                            <td className="py-2.5">individual investor (retail)</td>
                            <td className="py-2.5 text-right">
                                <Delta v={current?.nr_long_wow} />
                            </td>
                            <td className="py-2.5 text-right">
                                <Delta v={current?.nr_short_wow} />
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

function Delta({ v }) {
    if (v == null) return <span>&mdash;</span>;
    const color =
        v > 0 ? "rgba(184,212,208,0.95)" : v < 0 ? "rgba(212,160,160,0.9)" : null;
    return <span style={{ color }}>{fmtPct(v)}</span>;
}
